import React, { useState } from 'react';
import { AlertTriangle, Users, Clock } from 'lucide-react';
interface TriggerConfigProps {
  triggerAlert: string;
  personaFilter?: string;
  onTriggerChange?: (trigger: string) => void;
  onPersonaChange?: (persona: string) => void;
}
const TriggerConfiguration: React.FC<TriggerConfigProps> = ({
  triggerAlert,
  personaFilter,
  onTriggerChange,
  onPersonaChange
}) => {
  const [activeTab, setActiveTab] = useState<'trigger' | 'users'>('trigger');
  // Sample data for affected users
  const affectedUsers = [{
    name: 'Sarah Johnson',
    company: 'Acme Corp',
    email: 'sarah@acmecorp.com',
    lastEnrolled: null,
    nextReEnroll: null
  }, {
    name: 'Michael Chen',
    company: 'TechGlobal',
    email: 'mchen@techglobal.com',
    lastEnrolled: '2023-10-15',
    nextReEnroll: '2024-01-13'
  }, {
    name: 'David Williams',
    company: 'SaaS Solutions',
    email: 'david@saassolutions.com',
    lastEnrolled: null,
    nextReEnroll: null
  }, {
    name: 'Priya Patel',
    company: 'Innovate Inc',
    email: 'priya@innovateinc.com',
    lastEnrolled: '2023-11-05',
    nextReEnroll: '2024-02-03'
  }];
  // Alert options
  const alertOptions = ['Potential Analytics Upsell (High Manual Reporting)', 'Manufacturer Not Using Assemblies', 'User Overage - License Limit Exceeded', 'High MRR with Low Feature Adoption', 'Deal Closed-Won', 'Stagnant or Inactive Usage'];
  // Persona options
  const personaOptions = ['All Personas', 'Enterprise Customers', 'Mid-Market', 'SMB', 'Manufacturing', 'Distribution'];
  const handleTriggerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (onTriggerChange) {
      onTriggerChange(e.target.value);
    }
  };
  const handlePersonaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (onPersonaChange) {
      onPersonaChange(e.target.value);
    }
  };
  return <div className="mb-6 p-5 bg-white rounded-lg border" data-id="element-354">
      {/* Tab Navigation */}
      <div className="flex border-b mb-4" data-id="element-355">
        <button className={`px-4 py-2 text-sm font-medium ${activeTab === 'trigger' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('trigger')} data-id="element-356">
          Trigger Configuration
        </button>
        <button className={`px-4 py-2 text-sm font-medium ${activeTab === 'users' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('users')} data-id="element-357">
          Affected Users ({affectedUsers.length})
        </button>
      </div>
      {/* Trigger Configuration Tab */}
      {activeTab === 'trigger' && <div className="space-y-4" data-id="element-358">
          {/* Triggering Alert */}
          <div className="flex items-start" data-id="element-359">
            <div className="flex items-center justify-center w-8 h-8 bg-yellow-100 rounded-full mr-3 shrink-0" data-id="element-360">
              <AlertTriangle size={16} className="text-yellow-600" data-id="element-361" />
            </div>
            <div className="flex-1" data-id="element-362">
              <p className="text-sm font-medium mb-1" data-id="element-363">Triggering Alert</p>
              <div className="flex items-center" data-id="element-364">
                <select className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm" value={triggerAlert} onChange={handleTriggerChange} data-id="element-365">
                  {alertOptions.map(option => <option key={option} value={option} data-id="element-366">
                      {option}
                    </option>)}
                </select>
              </div>
            </div>
          </div>
          {/* Persona Filter */}
          <div className="flex items-start" data-id="element-367">
            <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full mr-3 shrink-0" data-id="element-368">
              <Users size={16} className="text-blue-600" data-id="element-369" />
            </div>
            <div className="flex-1" data-id="element-370">
              <p className="text-sm font-medium mb-1" data-id="element-371">Persona Filter</p>
              <div className="flex items-center" data-id="element-372">
                <select className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm" value={personaFilter || 'All Personas'} onChange={handlePersonaChange} data-id="element-373">
                  {personaOptions.map(option => <option key={option} value={option} data-id="element-374">
                      {option}
                    </option>)}
                </select>
              </div>
            </div>
          </div>
          {/* Schedule */}
          <div className="flex items-start" data-id="element-375">
            <div className="flex items-center justify-center w-8 h-8 bg-purple-100 rounded-full mr-3 shrink-0" data-id="element-376">
              <Clock size={16} className="text-purple-600" data-id="element-377" />
            </div>
            <div className="flex-1" data-id="element-378">
              <p className="text-sm font-medium mb-1" data-id="element-379">Schedule</p>
              <div className="grid grid-cols-2 gap-3" data-id="element-380">
                <div data-id="element-381">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-382">Check Cadence</p>
                  <select className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm" data-id="element-383">
                    <option data-id="element-384">Daily at 9 AM</option>
                    <option data-id="element-385">Weekly on Monday</option>
                    <option data-id="element-386">Monthly on the 1st</option>
                  </select>
                </div>
                <div data-id="element-387">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-388">Re-enrollment</p>
                  <select className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm" data-id="element-389">
                    <option data-id="element-390">Allow re-enrollment after 90 days</option>
                    <option data-id="element-391">Allow re-enrollment after 30 days</option>
                    <option data-id="element-392">Never allow re-enrollment</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>}
      {/* Affected Users Tab */}
      {activeTab === 'users' && <div data-id="element-393">
          <p className="text-sm text-gray-600 mb-3" data-id="element-394">
            Users who will be enrolled in this sequence when it's activated:
          </p>
          <div className="border rounded-md overflow-hidden" data-id="element-395">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-396">
              <thead className="bg-gray-50" data-id="element-397">
                <tr data-id="element-398">
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-399">
                    User
                  </th>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-400">
                    Company
                  </th>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-401">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-402">
                {affectedUsers.map((user, index) => <tr key={index} data-id="element-403">
                    <td className="px-4 py-2 whitespace-nowrap text-sm" data-id="element-404">
                      <div className="flex items-center" data-id="element-405">
                        <div className="h-7 w-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 mr-2" data-id="element-406">
                          {user.name.charAt(0)}
                        </div>
                        <div data-id="element-407">
                          <div className="font-medium" data-id="element-408">{user.name}</div>
                          <div className="text-gray-500 text-xs" data-id="element-409">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700" data-id="element-410">
                      {user.company}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm" data-id="element-411">
                      {user.lastEnrolled ? <div data-id="element-412">
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800" data-id="element-413">
                            Re-enrolls {user.nextReEnroll}
                          </span>
                        </div> : <div data-id="element-414">
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800" data-id="element-415">
                            New enrollment
                          </span>
                        </div>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}
    </div>;
};
export default TriggerConfiguration;